import { useState } from 'react';
import { useStore } from '../../context/StoreContext';
import { useToast } from '../../context/ToastContext';
import {
  Save,
  CheckCircle2,
  AlertCircle,
  CreditCard,
  Truck,
  Building,
  Upload,
  Image,
  Loader2,
} from 'lucide-react';

export function AdminSettings() {
  const { settings, refreshSettings } = useStore();
  const { showToast } = useToast();

  const [brandName, setBrandName] = useState(settings.brand_name || 'Aura Ateliê');
  const [brandTagline, setBrandTagline] = useState(settings.brand_tagline || '');
  const [brandDescription, setBrandDescription] = useState(settings.brand_description || '');
  const [contactEmail, setContactEmail] = useState(settings.contact_email || '');
  const [contactPhone, setContactPhone] = useState(settings.contact_phone || '');
  const [addressLine, setAddressLine] = useState(settings.address_line || '');
  const [cnpj, setCnpj] = useState(settings.cnpj || '');
  const [heroTitle, setHeroTitle] = useState(settings.hero_title || '');
  const [heroSubtitle, setHeroSubtitle] = useState(settings.hero_subtitle || '');
  const [heroImageUrl, setHeroImageUrl] = useState(settings.hero_image_url || '');
  const [announcementText, setAnnouncementText] = useState(settings.announcement_text || '');
  const [announcementEnabled, setAnnouncementEnabled] = useState(settings.announcement_enabled);
  const [shippingFlatRateReais, setShippingFlatRateReais] = useState(
    ((settings.shipping_flat_rate_cents || 2800) / 100).toFixed(2)
  );
  const [shippingFreeThresholdReais, setShippingFreeThresholdReais] = useState(
    ((settings.shipping_free_threshold_cents || 45000) / 100).toFixed(2)
  );

  const [saving, setSaving] = useState(false);
  const [uploadingHeroImage, setUploadingHeroImage] = useState(false);

  const handleUploadHeroImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingHeroImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('is_private', 'false');

      const res = await fetch('/api/admin/upload', {
        method: 'POST',
        body: formData,
      });

      const data = (await res.json()) as any;
      if (!res.ok) throw new Error(data.error || 'Falha ao enviar imagem.');

      setHeroImageUrl(data.url);
      showToast('success', 'Imagem enviada', 'A nova foto do hero foi salva no Cloudflare R2.');
    } catch (err: unknown) {
      showToast('error', 'Erro no upload', err instanceof Error ? err.message : 'Falha ao enviar arquivo.');
    } finally {
      setUploadingHeroImage(false);
      e.target.value = '';
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const flatCents = Math.round(parseFloat(shippingFlatRateReais.replace(',', '.')) * 100);
      const freeCents = Math.round(parseFloat(shippingFreeThresholdReais.replace(',', '.')) * 100);

      const payload = {
        brand_name: brandName,
        brand_tagline: brandTagline,
        brand_description: brandDescription,
        contact_email: contactEmail,
        contact_phone: contactPhone,
        address_line: addressLine,
        cnpj,
        hero_title: heroTitle,
        hero_subtitle: heroSubtitle,
        hero_image_url: heroImageUrl,
        announcement_text: announcementText,
        announcement_enabled: announcementEnabled,
        shipping_flat_rate_cents: flatCents,
        shipping_free_threshold_cents: freeCents,
      };

      const res = await fetch('/api/admin/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Falha ao salvar configurações.');

      showToast('success', 'Configurações atualizadas', 'As definições da loja foram salvas no Cloudflare D1.');
      await refreshSettings();
    } catch (err: unknown) {
      showToast('error', 'Erro', err instanceof Error ? err.message : 'Falha ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const isMercadoPagoReady = settings.payment_gateway_status === 'mercado_pago_ready';

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Header */}
      <div className="border-b border-border-subtle pb-6">
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary">
          Preferências Globais
        </span>
        <h1 className="text-2xl sm:text-3xl font-serif font-normal text-ink mt-0.5">
          Configurações da Marca & Operação
        </h1>
      </div>

      {/* Mercado Pago Deployment Checklist Indicator */}
      <div className="bg-surface border border-border-subtle p-6 rounded-xs shadow-subtle space-y-4">
        <div className="flex items-center justify-between border-b border-border-subtle pb-4">
          <div className="flex items-center gap-2.5">
            <CreditCard className="w-5 h-5 text-primary" />
            <h2 className="font-serif text-lg font-medium text-ink">
              Status do Gateway de Pagamento (Mercado Pago)
            </h2>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold ${
              isMercadoPagoReady ? 'bg-olive-light text-olive' : 'bg-amber-100 text-amber-900'
            }`}
          >
            {isMercadoPagoReady ? '● Mercado Pago Ativo em Produção' : '● Modo Teste Isolado Ativo'}
          </span>
        </div>

        <p className="text-xs text-ink-muted leading-relaxed">
          O adaptador de pagamento do Mercado Pago está completamente modelado na arquitetura. Por diretriz de segurança, transações de produção reais permanecem explicitamente bloqueadas até que as credenciais verdadeiras sejam inseridas nas variáveis secretas do Cloudflare Pages.
        </p>

        {/* Readiness Checklist */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-xs">
          <div className="p-3 bg-background-alt rounded-xs border border-border-subtle flex items-start gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-olive shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-ink">Mecanismo de Checkout & Reserva D1</p>
              <p className="text-[11px] text-ink-muted">Validação de idempotência e estoque atômica</p>
            </div>
          </div>

          <div className="p-3 bg-background-alt rounded-xs border border-border-subtle flex items-start gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-olive shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-ink">Simulador Determinístico de Testes</p>
              <p className="text-[11px] text-ink-muted">Valida aprovação, recusa, pendência e estorno</p>
            </div>
          </div>

          <div className="p-3 bg-background-alt rounded-xs border border-border-subtle flex items-start gap-2.5">
            {isMercadoPagoReady ? (
              <CheckCircle2 className="w-4 h-4 text-olive shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            )}
            <div>
              <p className="font-semibold text-ink">MERCADO_PAGO_ACCESS_TOKEN</p>
              <p className="text-[11px] text-ink-muted">
                {isMercadoPagoReady ? 'Configurado no Cloudflare Secrets' : 'Pendente (Aguardando chaves de produção)'}
              </p>
            </div>
          </div>

          <div className="p-3 bg-background-alt rounded-xs border border-border-subtle flex items-start gap-2.5">
            {isMercadoPagoReady ? (
              <CheckCircle2 className="w-4 h-4 text-olive shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            )}
            <div>
              <p className="font-semibold text-ink">MERCADO_PAGO_WEBHOOK_SECRET</p>
              <p className="text-[11px] text-ink-muted">
                {isMercadoPagoReady ? 'Assinatura HMAC x-signature verificada' : 'Pendente (Necessário para webhooks ao vivo)'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Settings Form */}
      <form onSubmit={handleSaveSettings} className="space-y-6">
        {/* Brand Information */}
        <div className="bg-surface p-6 rounded-xs border border-border-subtle shadow-subtle space-y-4">
          <div className="flex items-center gap-2 border-b border-border-subtle pb-3">
            <Building className="w-4 h-4 text-primary" />
            <h2 className="font-serif text-lg font-medium text-ink">Identidade da Marca & Loja</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            <div>
              <label className="font-semibold text-ink block mb-1">Nome da Marca</label>
              <input
                type="text"
                required
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="font-semibold text-ink block mb-1">Subtítulo / Tagline</label>
              <input
                type="text"
                value={brandTagline}
                onChange={(e) => setBrandTagline(e.target.value)}
                className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div className="text-xs">
            <label className="font-semibold text-ink block mb-1">Descrição Institucional</label>
            <textarea
              rows={2}
              value={brandDescription}
              onChange={(e) => setBrandDescription(e.target.value)}
              className="w-full bg-background border border-border-subtle p-3 rounded-xs focus:outline-none focus:border-primary"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
            <div>
              <label className="font-semibold text-ink block mb-1">E-mail de Atendimento</label>
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="font-semibold text-ink block mb-1">WhatsApp / Telefone</label>
              <input
                type="text"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="font-semibold text-ink block mb-1">CNPJ</label>
              <input
                type="text"
                value={cnpj}
                onChange={(e) => setCnpj(e.target.value)}
                className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary font-mono"
              />
            </div>
          </div>

          <div className="text-xs">
            <label className="font-semibold text-ink block mb-1">Endereço Físico do Ateliê</label>
            <input
              type="text"
              value={addressLine}
              onChange={(e) => setAddressLine(e.target.value)}
              className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
            />
          </div>
        </div>

        {/* Hero Section & Announcement Bar */}
        <div className="bg-surface p-6 rounded-xs border border-border-subtle shadow-subtle space-y-4 text-xs">
          <h2 className="font-serif text-lg font-medium text-ink border-b border-border-subtle pb-3">
            Destaques da Página Inicial & Barra de Avisos
          </h2>

          <div className="space-y-3">
            <div>
              <label className="font-semibold text-ink block mb-1">Título do Hero</label>
              <input
                type="text"
                value={heroTitle}
                onChange={(e) => setHeroTitle(e.target.value)}
                className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="font-semibold text-ink block mb-1">Subtítulo do Hero</label>
              <input
                type="text"
                value={heroSubtitle}
                onChange={(e) => setHeroSubtitle(e.target.value)}
                className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="font-semibold text-ink block mb-2">Imagem de Destaque da Página Inicial (Hero)</label>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start bg-background p-4 rounded-xs border border-border-subtle">
                {/* Visual Preview */}
                <div className="md:col-span-4 aspect-[4/5] bg-surface rounded-xs overflow-hidden border border-border-subtle relative shadow-subtle flex items-center justify-center">
                  {heroImageUrl ? (
                    <img
                      src={heroImageUrl}
                      alt="Pré-visualização do Hero"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-ink-muted p-4 text-center">
                      <Image className="w-8 h-8 mb-2 opacity-40" />
                      <span className="text-[11px]">Nenhuma imagem definida</span>
                    </div>
                  )}
                  <div className="absolute top-2 left-2 bg-ink/80 text-ink-inverted px-2 py-0.5 rounded-xs text-[10px] uppercase font-mono tracking-wider">
                    Preview
                  </div>
                </div>

                {/* Upload & URL Controls */}
                <div className="md:col-span-8 space-y-4">
                  <div>
                    <span className="block font-medium text-ink mb-1">Upload direto do seu computador</span>
                    <p className="text-[11px] text-ink-muted mb-3">
                      Envie sua própria foto em alta resolução (JPEG, PNG ou WebP). Proporção recomendada: 4:5 ou vertical.
                    </p>
                    <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-surface border border-border-subtle hover:border-primary text-ink rounded-xs cursor-pointer transition-colors shadow-subtle text-xs font-medium">
                      {uploadingHeroImage ? (
                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                      ) : (
                        <Upload className="w-4 h-4 text-primary" />
                      )}
                      <span>{uploadingHeroImage ? 'Enviando imagem...' : 'Escolher Foto do Computador'}</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleUploadHeroImage}
                        disabled={uploadingHeroImage}
                        className="hidden"
                      />
                    </label>
                  </div>

                  <div className="pt-3 border-t border-border-subtle">
                    <span className="block font-medium text-ink mb-1">Ou informe uma URL externa</span>
                    <input
                      type="url"
                      value={heroImageUrl}
                      onChange={(e) => setHeroImageUrl(e.target.value)}
                      placeholder="https://images.unsplash.com/..."
                      className="w-full bg-surface border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary font-mono text-[11px]"
                    />
                  </div>

                  {heroImageUrl && (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setHeroImageUrl('https://images.unsplash.com/photo-1616046229478-9901c5536a45?auto=format&fit=crop&w=1200&q=85')}
                        className="text-[11px] text-ink-muted hover:underline"
                      >
                        Restaurar imagem padrão
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="pt-3 border-t border-border-subtle space-y-3">
            <div className="flex items-center justify-between">
              <label className="font-semibold text-ink">Texto da Barra Superior de Avisos</label>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input
                  type="checkbox"
                  checked={announcementEnabled}
                  onChange={(e) => setAnnouncementEnabled(e.target.checked)}
                  className="rounded-xs text-primary focus:ring-primary"
                />
                <span>Exibir aviso</span>
              </label>
            </div>
            <input
              type="text"
              value={announcementText}
              onChange={(e) => setAnnouncementText(e.target.value)}
              className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
            />
          </div>
        </div>

        {/* Shipping & Handling */}
        <div className="bg-surface p-6 rounded-xs border border-border-subtle shadow-subtle space-y-4 text-xs">
          <div className="flex items-center gap-2 border-b border-border-subtle pb-3">
            <Truck className="w-4 h-4 text-primary" />
            <h2 className="font-serif text-lg font-medium text-ink">Configuração de Envio & Frete</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-semibold text-ink block mb-1">Taxa Fixa de Envio (R$)</label>
              <input
                type="text"
                value={shippingFlatRateReais}
                onChange={(e) => setShippingFlatRateReais(e.target.value)}
                className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary font-mono"
              />
            </div>

            <div>
              <label className="font-semibold text-ink block mb-1">Limite para Frete Cortesia (R$)</label>
              <input
                type="text"
                value={shippingFreeThresholdReais}
                onChange={(e) => setShippingFreeThresholdReais(e.target.value)}
                className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary font-mono"
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="bg-ink text-ink-inverted px-8 py-3 rounded-xs text-xs uppercase tracking-widest font-medium hover:bg-primary transition-colors flex items-center gap-2 shadow-elevated disabled:opacity-40"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Gravando no D1...' : 'Salvar Todas as Configurações'}
          </button>
        </div>
      </form>
    </div>
  );
}
